<template>
  <div class="w-80">
    <div class="flex justify-between items-center">
      <NText variant="caption1" weight="medium">
        {{ selectedAgreement?.agreementTitle }}
      </NText>

      <div v-if="!showDownloadButton">
        <NButton
          v-gid="{
            module: 'user',
            screen: 'home',
            section: 'approveAgreementDialog',
            component: 'downloadAgreementButton',
          }"
          variant="flat"
          color="green"
          size="small"
          class="text-brand-primary mx-auto cursor-pointer !rounded-full"
          @click="getAgreementText"
        >
          <NText variant="overline" weight="medium">
            {{ t('common.show') }}
          </NText>
        </NButton>
      </div>
    </div>

    <div class="flex items-center py-6">
      <NCheckbox v-model="checked">
        <NText variant="caption1" weight="medium">
          {{ t('agreement.acceptAgreementMessage') }}
        </NText>
      </NCheckbox>
    </div>

    <div class="flex gap-2">
      <NButton
        v-gid="{
          module: 'user',
          screen: 'home',
          section: 'approveAgreementDialog',
          component: 'signingAgreementButton',
        }"
        variant="fill"
        class="flex-1"
        :loading="isLoadingApproveAgreement"
        :disabled="!checked || isLoadingApproveAgreement"
        size="small"
        @click="
          approveAgreement({
            id: selectedAgreement.agreementId,
            approve: checked,
          })
        "
      >
        <NText variant="caption1" weight="bold">
          {{ t('agreement.signingAgreement') }}
        </NText>
      </NButton>

      <NButton
        variant="plain"
        color="gray"
        size="small"
        class="flex-1 text-neutral-primary"
        @click="close"
      >
        <NText variant="caption1" weight="bold">
          {{ t('common.cancel') }}
        </NText>
      </NButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { AgreementItem } from '@libs/api-reference';
import { useI18n } from 'vue-i18n';
import {
  useApproveAgreement,
  useGetAgreementText,
  USER_QUERY_KEYS,
} from '@core/user';
import { useDialog, useToast } from '@negar/core';
import { download } from '@libs/utils';
import { useQueryClient } from '@libs/use';

type ApproveAgreementDialogProps = {
  selectedAgreement: AgreementItem;
  showDownloadButton?: boolean;
};

type ApproveAgreementDialogEmits = {
  (e?: 'confirmCallback'): void;
};

const props = withDefaults(defineProps<ApproveAgreementDialogProps>(), {
  showDownloadButton: false,
});

const emits = defineEmits<ApproveAgreementDialogEmits>();

const checked = ref(false);

const queryClient = useQueryClient();

const toast = useToast();
const { t } = useI18n();
const { remove } = useDialog();

const close = () => {
  checked.value = false;

  remove();
};

const { getAgreementText } = useGetAgreementText({
  id: ref(props.selectedAgreement.agreementId),
  onSuccess(result) {
    download(result.data.title, result.data.attachment);
  },
});

const { approveAgreement, isLoadingApproveAgreement } = useApproveAgreement({
  async onSuccess() {
    toast.neutral({
      message: t('agreement.approveAgreementDoneMessage', {
        title: props.selectedAgreement?.agreementTitle,
      }),
      duration: 3000,
    });

    await queryClient.invalidateQueries(USER_QUERY_KEYS.agreements);

    remove();

    emits?.('confirmCallback');
  },
  onReject(error) {
    if (error) {
      for (const { message } of error.response.data) {
        toast.negative({
          message,
          duration: 3000,
        });
      }
    }
  },
});
</script>
